/**
 * Выделение в сетке — набор id, Ctrl/Cmd-клик, Shift-диапазон от якоря, «все
 * видимые», Esc. Общий для референсов Pinterest и экрана «Наши фото».
 *
 * `all` и `shown` раздельны намеренно: выделение живёт при смене фильтров
 * (выделил, переключил срез, довыделил), поэтому выпавшими считаются только id,
 * которых нет в `all` — в данных вообще; а индексы, Shift-диапазон и «все
 * видимые» считаются по `shown` — в том порядке, в каком элементы на экране.
 *
 * Якорь — индекс в `shown`, поэтому сбрасывается, когда `shown` меняется (по
 * identity). Это шире прежнего поведения References, где сброс шёл только по
 * смене фильтров и сортировки: теперь якорь теряется ещё и при перезапросе
 * данных — безвредно, диапазон просто начнётся с нового клика.
 */
import { useCallback, useEffect, useState } from "react";

export function useSelection<T extends { id: number }>(
  all: readonly T[],
  shown: readonly T[],
  escEnabled: boolean,
  /** Режим «клик выделяет» при первом рендере (экран помнит выбор). */
  initialSelecting = false,
): {
  selected: ReadonlySet<number>;
  selecting: boolean;
  setSelecting: (v: boolean) => void;
  toggle: (index: number, range: boolean) => void;
  selectVisible: () => void;
  clear: () => void;
} {
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [selecting, setSelecting] = useState(initialSelecting);
  const [anchor, setAnchor] = useState<number | null>(null);

  // Индексы уехали — якорь диапазона больше ничего не значит.
  useEffect(() => setAnchor(null), [shown]);

  // Элементы, исчезнувшие из данных, выпадают из выделения.
  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const ids = new Set(all.map((p) => p.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [all]);

  const clear = useCallback(() => {
    setSelected(new Set());
    setAnchor(null);
  }, []);

  // Esc снимает выделение, когда экран разрешил (закрыты просмотр и менеджеры;
  // поповеры свой Esc перехватывают раньше).
  useEffect(() => {
    if (!escEnabled || !selected.size) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") clear();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [escEnabled, selected.size, clear]);

  const toggle = useCallback(
    (index: number, range: boolean) => {
      const item = shown[index];
      if (!item) return;
      if (range && anchor !== null) {
        const [from, to] = anchor < index ? [anchor, index] : [index, anchor];
        setSelected((prev) => new Set([...prev, ...shown.slice(from, to + 1).map((p) => p.id)]));
      } else {
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(item.id)) next.delete(item.id);
          else next.add(item.id);
          return next;
        });
      }
      setAnchor(index);
    },
    [shown, anchor],
  );

  const selectVisible = useCallback(
    () => setSelected((prev) => new Set([...prev, ...shown.map((p) => p.id)])),
    [shown],
  );

  return { selected, selecting, setSelecting, toggle, selectVisible, clear };
}
